import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface BrandingConfig {
  appName: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  successColor: string;
  warningColor: string;
  errorColor: string;
  darkModePrimaryColor: string | null;
}

interface BrandingContextType {
  config: BrandingConfig;
  loading: boolean;
  refreshBranding: () => Promise<void>;
}

const defaultConfig: BrandingConfig = {
  appName: '3PM Escrow',
  logoUrl: null,
  faviconUrl: null,
  primaryColor: '#f97316',
  secondaryColor: '#ea580c',
  accentColor: '#3b82f6',
  successColor: '#10b981',
  warningColor: '#f59e0b',
  errorColor: '#ef4444',
  darkModePrimaryColor: null
};

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

export const BrandingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<BrandingConfig>(defaultConfig);
  const [loading, setLoading] = useState(true);

  const fetchBrandingConfig = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${apiUrl}/api/branding-config/public`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          const brandingData = result.data;
          setConfig(brandingData);
          localStorage.setItem('brandingConfig', JSON.stringify(brandingData));
        }
      } else {
        const cached = localStorage.getItem('brandingConfig');
        if (cached) {
          try {
            setConfig(JSON.parse(cached));
          } catch (e) {
            console.warn('Failed to parse cached branding config');
          }
        }
      }
    } catch (error) {
      console.warn('Failed to fetch branding config, using cached or default:', error);
      const cached = localStorage.getItem('brandingConfig');
      if (cached) {
        try {
          setConfig(JSON.parse(cached));
        } catch (e) {
          console.warn('Failed to parse cached branding config');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshBranding = async () => {
    setLoading(true);
    await fetchBrandingConfig();
  };

  useEffect(() => {
    fetchBrandingConfig();
  }, []);

  useEffect(() => {
    if (!config) return;

    const root = document.documentElement;
    
    root.style.setProperty('--color-primary', config.primaryColor);
    root.style.setProperty('--color-secondary', config.secondaryColor);
    root.style.setProperty('--color-accent', config.accentColor);
    root.style.setProperty('--color-success', config.successColor);
    root.style.setProperty('--color-warning', config.warningColor);
    root.style.setProperty('--color-error', config.errorColor);
    
    if (config.darkModePrimaryColor) {
      root.style.setProperty('--color-primary-dark', config.darkModePrimaryColor);
    }

    document.title = config.appName || '3PM Escrow';

    if (config.faviconUrl) {
      updateFavicon(config.faviconUrl);
    }
  }, [config]);

  const updateFavicon = (url: string) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const fullUrl = url.startsWith('http') ? url : `${apiUrl}${url}`;
      
      let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      link.href = fullUrl;

      const link32 = document.querySelector("link[rel~='icon'][sizes='32x32']") as HTMLLinkElement;
      if (link32) {
        link32.href = fullUrl;
      }

      const link16 = document.querySelector("link[rel~='icon'][sizes='16x16']") as HTMLLinkElement;
      if (link16) {
        link16.href = fullUrl;
      }
    } catch (error) {
      console.warn('Failed to update favicon:', error);
    }
  };

  return (
    <BrandingContext.Provider value={{ config, loading, refreshBranding }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = (): BrandingContextType => {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};
